import { ArrowDownTrayIcon, CalendarDaysIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { formatDate } from '../../../../common-utils/formatters';
import { PaymentStatus } from '../../../@types/PaymentStatus';
import type { RootState } from '../../../store/store';
import { getClientFollowUpNextActions } from '../../../utils/api';
import type { ClientActionStatus, ClientNextAction } from '../../../utils/clientFollowUp';
import {
  assignedUserLabel,
  assigneeOptions,
  CLIENT_ACTION_STATUS_CLASSES,
  CLIENT_ACTION_STATUS_LABELS,
  emptyActionLabel,
  formatActionDay,
  matchesFollowUpFilters,
  NO_ACTION_FILTER,
} from '../../../utils/clientFollowUp';
import type { CompanyExportColumn, ExportableCompany } from '../../../utils/companyListExport';
import { COMPANY_EXPORT_COLUMNS, downloadWorkbook, formatSetupSteps, writeCompanyListSheet } from '../../../utils/companyListExport';
import type { CompanyListFilters } from '../../../utils/companyListFilters';
import { createCompanyListFilters, filterCompanies, getDjActivities } from '../../../utils/companyListFilters';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';
import { ActivityBadges, CompanyListFiltersPanel, FederationLegend, FederationLogos } from '../components/CompanyListFilters';

// Subscription type enum for filtering
type SubscriptionType =
  | 'all'
  | PaymentStatus.UNLIMITED_FREE
  | PaymentStatus.ACTIVE_UNDER_1000
  | PaymentStatus.ACTIVE_1000_TO_2000
  | PaymentStatus.ACTIVE_OVER_2000;

// Helper function to format subscription status with custom styling
const formatSubscriptionStatus = (paymentStatus: string | null): { label: string; className: string } => {
  if (paymentStatus === PaymentStatus.UNLIMITED_FREE) {
    return { label: 'Illimité gratuit', className: 'bg-blue-100 text-blue-800' };
  }
  if (paymentStatus === PaymentStatus.ACTIVE_UNDER_1000) {
    return { label: 'Actif < 1000 lots', className: 'bg-green-50 text-green-700' };
  }
  if (paymentStatus === PaymentStatus.ACTIVE_1000_TO_2000) {
    return { label: 'Actif 1000-2000 lots', className: 'bg-green-100 text-green-800' };
  }
  if (paymentStatus === PaymentStatus.ACTIVE_OVER_2000 || paymentStatus === PaymentStatus.ACTIVE) {
    return { label: 'Actif > 2000 lots', className: 'bg-green-200 text-green-900' };
  }
  return { label: 'Inconnu', className: 'bg-orange-100 text-orange-800' };
};

// Helper to normalize payment status for filtering
const normalizePaymentStatus = (paymentStatus: string | null): SubscriptionType | null => {
  if (paymentStatus === PaymentStatus.UNLIMITED_FREE) {return PaymentStatus.UNLIMITED_FREE;}
  if (paymentStatus === PaymentStatus.ACTIVE_UNDER_1000) {return PaymentStatus.ACTIVE_UNDER_1000;}
  if (paymentStatus === PaymentStatus.ACTIVE_1000_TO_2000) {return PaymentStatus.ACTIVE_1000_TO_2000;}
  if (paymentStatus === PaymentStatus.ACTIVE_OVER_2000 || paymentStatus === PaymentStatus.ACTIVE) {return PaymentStatus.ACTIVE_OVER_2000;}
  return null;
};

interface ClientListItem extends ExportableCompany {
  siren: string;
  paymentStatus: string | null;
  followUp?: ClientNextAction;
}

const NEXT_ACTIONS_PER_PAGE = 500;
const NEXT_ACTION_STATUSES: ClientActionStatus[] = ['overdue', 'soon', 'upcoming'];

// The company list export, with the subscription after the SIREN
const [NAME_COLUMN, SIREN_COLUMN, ...OTHER_COLUMNS] = COMPANY_EXPORT_COLUMNS;
const CLIENT_EXPORT_COLUMNS: CompanyExportColumn<ClientListItem>[] = [
  NAME_COLUMN,
  SIREN_COLUMN,
  { header: 'ABONNEMENT', width: 20, value: (client) => formatSubscriptionStatus(client.paymentStatus).label },
  {
    header: 'PROCHAINE ACTION',
    width: 40,
    value: ({ followUp }) => {
      if (!followUp) {return '';}
      return followUp.nextAction?.title ?? emptyActionLabel(followUp);
    },
  },
  { header: 'ÉCHÉANCE', width: 14, value: ({ followUp }) => (followUp?.nextAction ? formatActionDay(followUp.nextAction.date) : '') },
  { header: 'STATUT', width: 12, value: ({ followUp }) => (followUp?.nextAction ? CLIENT_ACTION_STATUS_LABELS[followUp.nextAction.status] : '') },
  { header: 'ASSIGNÉ À', width: 24, value: ({ followUp }) => (followUp?.nextAction ? assignedUserLabel(followUp.nextAction.assignedUser) : '') },
  ...OTHER_COLUMNS,
];

function ClientsList() {
  const [companies, setCompanies] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCCI, setLoadingCCI] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [filters, setFilters] = useState<CompanyListFilters>(createCompanyListFilters);
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType>('all');
  const [nextActions, setNextActions] = useState<ClientNextAction[]>([]);
  const [loadingNextActions, setLoadingNextActions] = useState(true);
  const [nextActionStatus, setNextActionStatus] = useState('');
  const [nextActionAssignee, setNextActionAssignee] = useState('');

  const [exporting, setExporting] = useState(false);

  const fetchCompaniesPage = async(page = 1, limit = 1000, existingCompanies: ClientListItem[] = []) => {
    try {
      // Note: clientsOnly=true filters to only show companies with active subscriptions or unlimited free
      const response = await fetch(`/api/companies?step=${page}&limit=${limit}&includeCCI=true&clientsOnly=true`, JWTHeader(jwt));
      const data = await response.json();
      
      // Use the pagination data returned from the API
      const totalPages = data.pagination?.totalPages;
      setLoadingProgress(Math.floor((page / totalPages) * 100));
      
      const newCompanies = data.results.map(company => {
        const details = company.details || {};
        return {
          ...company,
          activities: getDjActivities(details),
          nbLotsHab: isNaN(Number(details.nbLotsHab)) ? 0 : Number(details.nbLotsHab),
          nbLotsTotal: isNaN(Number(details.nbLotsHabBurCom)) ? 0 : Number(details.nbLotsHabBurCom),
        };
      });
      
      const allCompanies = [...existingCompanies, ...newCompanies];
      
      // Update state with current batch of companies
      setCompanies(allCompanies);
      
      // Check if there are more pages to fetch
      if (data.pagination?.nextPage) {
        // Fetch next page using the nextPage value from pagination
        await fetchCompaniesPage(data.pagination.nextPage, limit, allCompanies);
        return;
      }
      
      // All pages fetched
      setLoading(false);
      setLoadingCCI(false);
      setLoadingProgress(100);
    } catch (error) {
      console.error('Error fetching companies page:', error);
      showToast('error', 'Une erreur est survenue lors du chargement des clients.');
      setLoading(false);
      setLoadingCCI(false);
    }
  };

  const fetchNextActionsPage = async(page: number, existing: ClientNextAction[]): Promise<ClientNextAction[]> => {
    const response = await fetch(getClientFollowUpNextActions(page, NEXT_ACTIONS_PER_PAGE), JWTHeader(jwt));
    if (!response.ok) {throw new Error(`HTTP ${response.status}`);}
    const data = await response.json();
    const all = [...existing, ...data.results];
    return data.pagination?.nextPage ? fetchNextActionsPage(data.pagination.nextPage, all) : all;
  };

  const fetchNextActions = async() => {
    try {
      setNextActions(await fetchNextActionsPage(1, []));
    } catch (error) {
      console.error('Error fetching next actions:', error);
      showToast('error', 'Une erreur est survenue lors du chargement des prochaines actions.');
    } finally {
      setLoadingNextActions(false);
    }
  };

  useEffect(() => {
    if (!jwt) {return;}
    // Start fetching from page 1 with empty initial companies array
    fetchCompaniesPage(1, 500, []);
    fetchNextActions();
  }, [jwt]);

  const clients = useMemo(() => {
    const followUps = new Map(nextActions.map((followUp) => [followUp.companyId, followUp]));
    return companies.map((company) => ({ ...company, followUp: followUps.get(company.id) }));
  }, [companies, nextActions]);

  const filteredCompanies = useMemo(
    () => filterCompanies(clients, filters).filter((company) => (subscriptionType === 'all'
      || normalizePaymentStatus(company.paymentStatus) === subscriptionType)
      && matchesFollowUpFilters(company.followUp, nextActionStatus, nextActionAssignee)),
    [clients, filters, subscriptionType, nextActionStatus, nextActionAssignee],
  );

  const assignees = useMemo(
    () => assigneeOptions(nextActions.flatMap((followUp) => (followUp.nextAction ? [followUp.nextAction] : []))),
    [nextActions],
  );

  const handleViewCompany = (companyId: number) => {
    window.open(ROUTES.ADMIN_COMPANY_DETAILS.replace(':companyId', companyId.toString()), '_blank');
  };

  const headers = [
    { title: 'NOM', class: 'w-1/10' },
    { title: 'SIREN', class: 'w-1/10' },
    { title: 'ABONNEMENT', class: 'w-1/10' },
    { title: 'PROCHAINE ACTION', class: 'w-1/10' },
    { title: 'ÉCHÉANCE', class: 'w-1/10' },
    { title: 'STATUT', class: 'w-1/10' },
    { title: 'ASSIGNÉ À', class: 'w-1/10' },
    { title: 'FÉDÉRATION', class: 'w-1/10' },
    { title: 'ACTIVITÉ', class: 'w-1/10' },
    { title: 'NB LOTS HAB', class: 'w-1/10' },
    { title: 'NB LOTS HAB / BUR / COM', class: 'w-1/10' },
    { title: 'DATE FIN CARTE', class: 'w-1/10' },
    { title: 'ÉTAPE CONFIG VALIDE', class: 'w-1/10' },
    { title: 'ACTIONS', class: 'w-1/10' },
  ];

  const formattedData = filteredCompanies.map((company) => {
    const subscriptionStatus = formatSubscriptionStatus(company.paymentStatus);
    const { followUp } = company;
    const nextAction = followUp?.nextAction ?? null;
    const followUpPlaceholder = loadingNextActions ? <div className="h-4 w-24 animate-pulse rounded bg-gray-200" /> : '';
    return {
      NOM: company.name,
      SIREN: company.siren,
      ABONNEMENT: (
        <span className={`inline-flex items-center rounded-lg px-3 py-1 text-sm font-medium ${subscriptionStatus.className}`}>
          {subscriptionStatus.label}
        </span>
      ),
      'PROCHAINE ACTION': followUp && nextAction ? (
        <div className="min-w-56">
          <div>{nextAction.title}</div>
          <div className="text-sm text-gray-600">
            {[nextAction.eventName, followUp.overdueCount > 1 && `${followUp.overdueCount} échéances en retard`].filter(Boolean).join(' · ')}
          </div>
          {nextAction.status === 'overdue' && followUp.upcomingAction && (
            <div className="text-sm text-gray-600">
              Ensuite : {followUp.upcomingAction.eventName ?? followUp.upcomingAction.title}, le {formatActionDay(followUp.upcomingAction.date)}
            </div>
          )}
        </div>
      ) : (followUp ? <span className="text-gray-500">{emptyActionLabel(followUp)}</span> : followUpPlaceholder),
      ÉCHÉANCE: nextAction ? formatActionDay(nextAction.date) : followUpPlaceholder,
      STATUT: nextAction ? (
        <span className={`inline-flex items-center rounded-lg px-3 py-1 text-sm font-medium whitespace-nowrap ${CLIENT_ACTION_STATUS_CLASSES[nextAction.status]}`}>
          {CLIENT_ACTION_STATUS_LABELS[nextAction.status]}
        </span>
      ) : followUpPlaceholder,
      'ASSIGNÉ À': nextAction ? assignedUserLabel(nextAction.assignedUser) : followUpPlaceholder,
      FÉDÉRATION: <FederationLogos details={company.details} />,
      ACTIVITÉ: <ActivityBadges details={company.details} loading={loadingCCI} />,
      'NB LOTS HAB': company.nbLotsHab.toLocaleString('fr-FR'),
      'NB LOTS HAB / BUR / COM': company.nbLotsTotal.toLocaleString('fr-FR'),
      'DATE FIN CARTE': loadingCCI ? (
        <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
      ) : (
        company.details.dateFinCarte ? formatDate(company.details.dateFinCarte) : ''
      ),
      'ÉTAPE CONFIG VALIDE': formatSetupSteps(company.setupSteps),
      ACTIONS: (
        <button
          onClick={() => handleViewCompany(company.id)}
          className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto"
        >
          Afficher la fiche entreprise
        </button>
      ),
    };
  });

  const exportToExcel = async() => {
    if (exporting) {return;}

    if (loading || loadingNextActions) {
      showToast('warning', 'Veuillez attendre que les données soient chargées.');
      return;
    }

    if (filteredCompanies.length === 0) {
      showToast('warning', 'Aucune donnée à exporter. Veuillez ajuster vos filtres.');
      return;
    }

    setExporting(true);

    try {
      const workbook = new ExcelJS.Workbook();
      writeCompanyListSheet(workbook.addWorksheet('Liste des clients'), CLIENT_EXPORT_COLUMNS, filteredCompanies);
      await downloadWorkbook(workbook, `Liste_clients_${new Date().toISOString()}.xlsx`);

      showToast('success', 'Export réussi.');
    } catch (error) {
      console.error('Error exporting to Excel :', error);
      showToast('error', 'Une erreur est survenue lors de l\'export.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      
      <div className="rounded-lg bg-white p-4 shadow-md">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h1 className="flex items-center text-2xl font-bold text-gray-800">
            <UserGroupIcon className="mr-2 h-8 w-8 text-rgpd-blue-dark" />
            Clients
          </h1>
          <Link to={ROUTES.ADMIN_CLIENTS_PLANNING} className="flex items-center text-sm font-medium text-rgpd-blue-dark hover:underline">
            <CalendarDaysIcon className="mr-1 h-5 w-5" />
            Voir le planning de tous les clients
          </Link>
        </div>
        
        {/* Add note about clients list */}
        <div className="mb-4 text-sm text-gray-600">
          <p>Cette liste affiche uniquement les entreprises ayant un abonnement actif ou un accès illimité gratuit.</p>
          <p className="mt-1">Note : Les données concernant le nombre de lots proviennent directement du registre national d'immatriculation et non des informations saisies par l'entreprise.</p>
        </div>

        <FederationLegend />
        
        <div className="mt-8 mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">Filtres</h2>
          <button
            onClick={exportToExcel}
            className="flex items-center rounded bg-rgpd-green px-4 py-2 text-white transition-colors hover:bg-rgpd-green-dark"
            disabled={loading || loadingNextActions || exporting}
          >
            {exporting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Exportation en cours...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
                Exporter la liste
              </>
            )}
          </button>
        </div>
        <CompanyListFiltersPanel filters={filters} onChange={setFilters}>
          <div>
            <label htmlFor="clients-subscription-type" className="mb-2 block font-semibold">Type d'abonnement</label>
            <select
              id="clients-subscription-type"
              name="subscriptionType"
              value={subscriptionType}
              onChange={(e) => setSubscriptionType(e.target.value as SubscriptionType)}
              className="w-full rounded border p-2"
            >
              <option value="all">Tous</option>
              <option value={PaymentStatus.UNLIMITED_FREE}>Illimité gratuit</option>
              <option value={PaymentStatus.ACTIVE_UNDER_1000}>Actif &lt; 1000 lots</option>
              <option value={PaymentStatus.ACTIVE_1000_TO_2000}>Actif 1000-2000 lots</option>
              <option value={PaymentStatus.ACTIVE_OVER_2000}>Actif &gt; 2000 lots</option>
            </select>
          </div>
          <div>
            <label htmlFor="clients-next-action-status" className="mb-2 block font-semibold">Statut de la prochaine action</label>
            <select
              id="clients-next-action-status"
              name="nextActionStatus"
              value={nextActionStatus}
              onChange={(e) => setNextActionStatus(e.target.value)}
              className="w-full rounded border p-2"
            >
              <option value="">Tous</option>
              {NEXT_ACTION_STATUSES.map((status) => (
                <option key={status} value={status}>{CLIENT_ACTION_STATUS_LABELS[status]}</option>
              ))}
              <option value={NO_ACTION_FILTER}>Aucune action à venir</option>
            </select>
          </div>
          <div>
            <label htmlFor="clients-next-action-assignee" className="mb-2 block font-semibold">Prochaine action assignée à</label>
            <select
              id="clients-next-action-assignee"
              name="nextActionAssignee"
              value={nextActionAssignee}
              onChange={(e) => setNextActionAssignee(e.target.value)}
              className="w-full rounded border p-2"
            >
              <option value="">Toutes les personnes</option>
              {assignees.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </CompanyListFiltersPanel>
        {loading ? (
          <div className="flex h-64 flex-col items-center justify-center">
            <Spinner size="xl" />
            <div className="mt-4 w-64">
              <div className="h-2.5 w-full rounded-full bg-gray-200">
                <div 
                  className="h-2.5 rounded-full bg-rgpd-blue-dark" 
                  style={{ width: `${loadingProgress}%` }}
                ></div>
              </div>
              <p className="mt-2 text-center text-sm text-gray-600">
                Chargement des clients : {loadingProgress}%
              </p>
            </div>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="flex h-64 items-center justify-center">
            <Badge
              mode="warning"
              text="Aucun client ne correspond aux critères sélectionnés."
              size="large"
              type="solid"
            />
          </div>
        ) : (
          <MUITable
            data={formattedData}
            headers={headers}
            tableClass="w-full"
            isSearchable
            sortableColumns={['NOM', 'SIREN', 'ÉCHÉANCE', 'ASSIGNÉ À', 'NB LOTS HAB', 'NB LOTS HAB / BUR / COM', 'ÉTAPE CONFIG VALIDE']}
            defaultSortKey="ÉCHÉANCE"
          />
        )}
      </div>
    </div>
  );
}

export default ClientsList;
